import { useState } from "react";
import { Sparkles, X, ArrowRight, Check, TrendingUp, Eye, Search, Zap, Target, Coins } from "lucide-react";
import { useBranding } from "../context/BrandingContext";

const BENEFITS = [
  {
    id: "viral", icon: TrendingUp, text: "Potencial Viral", subtitle: "Predicción del rendimiento",
    accent: "red",
    title: "Descubre si tu video tiene lo que hace falta para explotar",
    summary: "Nuestra IA evalúa hook, curiosidad, ritmo, emoción y retención para darte una puntuación de 0 a 100. No es una opinión: es un diagnóstico basado en los patrones que comparten los videos que sí se hacen virales.",
    bullets: ["Score de viralidad con desglose por factor", "Los 3 puntos débiles que te frenan y cómo corregirlos", "Predicción de vistas: conservadora, esperada y optimista"],
  },
  {
    id: "retencion", icon: Eye, text: "Retención", subtitle: "Momentos fuertes y débiles",
    accent: "cyan",
    title: "Sabe exactamente en qué segundo pierdes a tu audiencia",
    summary: "YouTube premia los videos que mantienen a la gente viendo. Analizamos tu contenido bloque por bloque para detectar caídas de atención, partes repetitivas y momentos que deberías acortar o reforzar.",
    bullets: ["Curva de retención estimada por bloques de tiempo", "Motivo de cada caída y la solución concreta", "Loops narrativos que enganchan hasta el final"],
  },
  {
    id: "seo", icon: Search, text: "SEO", subtitle: "Palabras clave y posicionamiento",
    accent: "magenta",
    title: "Haz que YouTube y Google te encuentren primero",
    summary: "Un gran video sin SEO es invisible. Extraemos las palabras clave que realmente busca tu audiencia y te proponemos títulos optimizados para clics y sugerencias de miniatura que compiten en el feed.",
    bullets: ["Keywords principales detectadas en tu contenido", "3 títulos optimizados: SEO, CTR y viral", "Ideas de miniatura alineadas con tu título"],
  },
  {
    id: "hooks", icon: Zap, text: "Hooks y CTAs", subtitle: "Inicio, estructura y llamadas a la acción",
    accent: "violet",
    title: "Los primeros 8 segundos deciden todo",
    summary: "Si el gancho no atrapa, el resto no importa. Puntuamos tus hooks de apertura, evaluamos cada llamada a la acción y te decimos cómo reformularlos para que más gente se quede, comente y se suscriba.",
    bullets: ["Hook principal puntuado con mejora sugerida", "Efectividad y ubicación de cada CTA", "Disparadores psicológicos que activas (y los que no)"],
  },
  {
    id: "crecimiento", icon: Target, text: "Oportunidades de Crecimiento", subtitle: "Acciones concretas para mejorar",
    accent: "cyan",
    title: "Un plan de acción priorizado, no una lista de deseos",
    summary: "Convertimos el análisis en pasos concretos ordenados por impacto y esfuerzo. Sabrás qué cambiar primero para ganar más vistas con el menor trabajo posible, y qué puedes dejar para después.",
    bullets: ["Oportunidades ordenadas por impacto estimado", "Prioridad crítica / alta / media con esfuerzo requerido", "Resumen ejecutivo con fortalezas y debilidades"],
  },
  {
    id: "monetizacion", icon: Coins, text: "Monetización", subtitle: "RPM, CPM e ingresos potenciales",
    accent: "magenta",
    title: "Convierte cada vista en más ingresos",
    summary: "No basta con crecer: hay que rentabilizar. La IA detecta el potencial económico de tu video y te recomienda la estrategia de monetización más adecuada según tu nicho, audiencia y contenido.",
    bullets: ["Potencial de afiliados, patrocinios y productos propios", "Estimación de RPM/CPM e ingresos por cada 1.000 vistas", "Puntos exactos del video para integrar ofertas sin perder retención"],
  },
];

const ACCENTS = {
  red: { border: "border-brand-red/50 hover:border-brand-red/90", glow: "hover:shadow-[0_0_0_1px_rgba(224,0,0,0.35),0_16px_40px_-12px_rgba(224,0,0,0.7)]", active: "border-brand-red/80 shadow-[0_0_0_1px_rgba(224,0,0,0.45),0_12px_32px_-12px_rgba(224,0,0,0.6)]", icon: "text-brand-red", iconBox: "bg-brand-red/15 border-brand-red/50 shadow-[0_0_14px_rgba(224,0,0,0.35)] group-hover:border-brand-red/90 group-hover:shadow-[0_0_26px_rgba(224,0,0,0.65)]", label: "text-[#FF5A5A]", pointer: "bg-brand-red/20 border-brand-red/40", panelBorder: "border-brand-red/35", panelGlow: "shadow-[0_0_0_1px_rgba(224,0,0,0.25),0_24px_60px_-24px_rgba(224,0,0,0.5)]", check: "text-[#FF5A5A]" },
  violet: { border: "border-purple-400/50 hover:border-purple-300/90", glow: "hover:shadow-[0_0_0_1px_rgba(139,92,246,0.35),0_16px_40px_-12px_rgba(139,92,246,0.75)]", active: "border-purple-400/80 shadow-[0_0_0_1px_rgba(139,92,246,0.45),0_12px_32px_-12px_rgba(139,92,246,0.6)]", icon: "text-purple-300", iconBox: "bg-purple-500/15 border-purple-400/50 shadow-[0_0_14px_rgba(139,92,246,0.35)] group-hover:border-purple-300/90 group-hover:shadow-[0_0_26px_rgba(139,92,246,0.7)]", label: "text-purple-300", pointer: "bg-purple-500/20 border-purple-400/40", panelBorder: "border-purple-500/35", panelGlow: "shadow-[0_0_0_1px_rgba(139,92,246,0.25),0_24px_60px_-24px_rgba(139,92,246,0.5)]", check: "text-purple-300" },
  magenta: { border: "border-pink-400/50 hover:border-pink-300/90", glow: "hover:shadow-[0_0_0_1px_rgba(236,72,153,0.35),0_16px_40px_-12px_rgba(236,72,153,0.75)]", active: "border-pink-400/80 shadow-[0_0_0_1px_rgba(236,72,153,0.45),0_12px_32px_-12px_rgba(236,72,153,0.6)]", icon: "text-pink-300", iconBox: "bg-pink-500/15 border-pink-400/50 shadow-[0_0_14px_rgba(236,72,153,0.35)] group-hover:border-pink-300/90 group-hover:shadow-[0_0_26px_rgba(236,72,153,0.7)]", label: "text-pink-300", pointer: "bg-pink-500/20 border-pink-400/40", panelBorder: "border-pink-500/35", panelGlow: "shadow-[0_0_0_1px_rgba(236,72,153,0.25),0_24px_60px_-24px_rgba(236,72,153,0.5)]", check: "text-pink-300" },
  cyan: { border: "border-cyan-400/50 hover:border-cyan-300/90", glow: "hover:shadow-[0_0_0_1px_rgba(6,182,212,0.35),0_16px_40px_-12px_rgba(6,182,212,0.75)]", active: "border-cyan-400/80 shadow-[0_0_0_1px_rgba(6,182,212,0.45),0_12px_32px_-12px_rgba(6,182,212,0.6)]", icon: "text-cyan-300", iconBox: "bg-cyan-500/15 border-cyan-400/50 shadow-[0_0_14px_rgba(6,182,212,0.35)] group-hover:border-cyan-300/90 group-hover:shadow-[0_0_26px_rgba(6,182,212,0.7)]", label: "text-cyan-300", pointer: "bg-cyan-500/20 border-cyan-400/40", panelBorder: "border-cyan-500/35", panelGlow: "shadow-[0_0_0_1px_rgba(6,182,212,0.25),0_24px_60px_-24px_rgba(6,182,212,0.5)]", check: "text-cyan-300" },
};

const scrollToAnalyzer = () => {
  const el = document.querySelector('[data-testid="script-input"]');
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  setTimeout(() => el.focus({ preventScroll: true }), 500);
};

const BenefitPanel = ({ benefit, index, total, onClose }) => {
  const a = ACCENTS[benefit.accent];
  return (
  <div data-testid={`hero-benefit-panel-${benefit.id}`} className="relative mt-5 text-left animate-cardIn">
    <div
      className={`absolute -top-2 w-4 h-4 rotate-45 border-l border-t ${a.pointer} bg-brand-card transition-[left] duration-300`}
      style={{ left: `calc(${((index + 0.5) / total) * 100}% - 8px)` }}
    />
    <div className={`bg-brand-card/90 backdrop-blur-xl rounded-2xl p-6 sm:p-8 border ${a.panelBorder} ${a.panelGlow}`}>
      <button type="button" onClick={onClose} data-testid="hero-benefit-panel-close" aria-label="Cerrar" className="absolute top-4 right-4 p-1.5 rounded-full bg-white/15 hover:bg-white/30 transition-colors">
        <X className="w-4 h-4" />
      </button>
      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`w-16 h-16 shrink-0 rounded-2xl border flex items-center justify-center ${a.iconBox}`}>
          <benefit.icon className={`w-8 h-8 ${a.icon}`} strokeWidth={1.75} />
        </div>
        <div className="flex-1">
          <p className={`text-xs font-bold uppercase tracking-widest ${a.label} mb-1`}>{benefit.text}</p>
          <h3 className="text-xl md:text-2xl font-bold mb-3">{benefit.title}</h3>
          <p className="text-white/90 text-sm md:text-base leading-relaxed mb-5">{benefit.summary}</p>
          <ul className="grid sm:grid-cols-3 gap-3 mb-6">
            {benefit.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm bg-white/10 rounded-xl px-3 py-2.5 border border-white/10">
                <Check className={`w-4 h-4 mt-0.5 shrink-0 ${a.check}`} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <button type="button" onClick={scrollToAnalyzer} data-testid="hero-benefit-panel-cta" className="vt-btn-primary vt-cta-glow rounded-full">
            Analizar mi video ahora <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  </div>
  );
};

export default function HeroSection() {
  const { branding } = useBranding();
  const [active, setActive] = useState(null);
  const activeIndex = BENEFITS.findIndex((b) => b.id === active);

  return (
    <section data-testid="hero-section" className="vt-dark-zone relative overflow-hidden bg-gradient-to-b from-brand-bg via-[#140f3a] to-brand-bg text-white py-20 pb-24">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(139,92,246,0.34)_0%,rgba(5,8,22,0)_62%)]"></div>
      <div className="pointer-events-none absolute left-1/2 top-[36%] -translate-x-1/2 -translate-y-1/2 w-[900px] h-[560px] rounded-full bg-purple-600/40 blur-[150px]"></div>
      <div className="pointer-events-none absolute right-[8%] top-[12%] w-[340px] h-[340px] rounded-full bg-cyan-500/25 blur-[120px]"></div>
      <div className="pointer-events-none absolute left-[6%] top-[55%] w-[300px] h-[300px] rounded-full bg-brand-red/20 blur-[130px]"></div>
      <div className="pointer-events-none absolute left-1/2 bottom-[-100px] -translate-x-1/2 w-[640px] h-[360px] rounded-full bg-pink-600/28 blur-[130px]"></div>
      <div className="pointer-events-none absolute inset-0 opacity-[0.07] bg-[linear-gradient(rgba(255,255,255,0.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.6)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]"></div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-brand-bg to-transparent"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center gap-3 vt-glass px-6 py-3 rounded-full mb-8 animate-fadeIn border-brand-red/30 shadow-[0_0_24px_-6px_rgba(224,0,0,0.45)]">
          <Sparkles className="w-6 h-6 text-brand-red drop-shadow-[0_0_8px_rgba(224,0,0,0.7)]" />
          <span className="text-2xl font-bold">{branding.app_name}</span>
        </div>

        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight animate-fadeIn">
          Analiza cualquier video de YouTube<br />
          con <span className="vt-gradient-text vt-gradient-text-glow">Inteligencia Artificial</span>
        </h1>

        <p className="text-lg sm:text-xl text-brand-text2 max-w-3xl mx-auto mb-10 animate-fadeIn">
          Descubre cómo aumentar vistas, retención, CTR, SEO y crecimiento del canal.
        </p>

        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {BENEFITS.map((benefit, index) => {
              const isActive = active === benefit.id;
              const a = ACCENTS[benefit.accent];
              return (
                <button
                  type="button"
                  key={benefit.id}
                  data-testid={`hero-benefit-${index}`}
                  aria-expanded={isActive}
                  onClick={() => setActive(isActive ? null : benefit.id)}
                  style={{ animationDelay: `${300 + index * 120}ms` }}
                  className={`group animate-cardIn h-full min-h-[150px] flex flex-col items-center justify-center bg-brand-card rounded-xl p-4 border transition-all duration-300 hover:-translate-y-1 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70 ${a.border} ${a.glow} ${isActive ? `${a.active} -translate-y-1` : ""} ${active && !isActive ? "opacity-60" : ""}`}
                >
                  <span className={`w-12 h-12 mx-auto mb-3 rounded-xl border flex items-center justify-center transition-all duration-300 ${a.iconBox}`}>
                    <benefit.icon className={`w-6 h-6 ${a.icon}`} strokeWidth={1.75} />
                  </span>
                  <p className="text-sm font-bold leading-tight text-white">{benefit.text}</p>
                  <p className="text-[11px] leading-snug text-brand-text2/80 mt-1">{benefit.subtitle}</p>
                </button>
              );
            })}
          </div>

          <p data-testid="hero-factors-line" className="mt-6 text-sm text-brand-text2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 animate-fadeIn">
            {["Hooks", "Retención", "SEO", "CTR", "Storytelling", "Monetización"].map((f) => (
              <span key={f} className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-purple-300" />{f}</span>
            ))}
            <span className="text-white/70">y más de 30 factores analizados con IA</span>
          </p>

          {activeIndex >= 0 && (
            <BenefitPanel
              key={active}
              benefit={BENEFITS[activeIndex]}
              index={activeIndex}
              total={BENEFITS.length}
              onClose={() => setActive(null)}
            />
          )}
        </div>

        {!active && (
          <div className="mt-12 animate-bounce">
            <div className="w-8 h-8 border-2 border-white rounded-full mx-auto flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
